import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@luacentrix/ui';
import { useApp } from '../app-state';
import { BrandLogo } from './BrandMark';

export const DISCORD_INVITE = 'https://discord.gg/v4MnRRwrV7';

interface ChromeProps {
  home?: boolean;
}

interface PublicLinks {
  signInHref: string;
  signedIn: boolean;
}

function usePublicLinks(): PublicLinks {
  const { api, account } = useApp();
  return {
    signInHref: `${api.config.baseUrl}/auth/discord/start`,
    signedIn: account.status === 'signed_in',
  };
}

function sectionHref(home: boolean, hash: string): string {
  return home ? hash : `/${hash}`;
}

export function PublicTopbar({ home = false }: ChromeProps): ReactElement {
  const { signInHref, signedIn } = usePublicLinks();
  return (
    <header className="landing__topbar">
      <div className="landing__inner landing__topbar-row">
        <Link aria-label="LuaCentrix home" className="landing__brand" to="/">
          <BrandLogo />
        </Link>
        <nav aria-label="Sections" className="landing__nav">
          <Link className="landing__nav-link" to="/try">
            Free tool
          </Link>
          <a className="landing__nav-link" href={sectionHref(home, '#features')}>
            Features
          </a>
          <a className="landing__nav-link" href={sectionHref(home, '#pricing')}>
            Pricing
          </a>
          <a className="landing__nav-link" href={sectionHref(home, '#faq')}>
            FAQ
          </a>
          <Link className="landing__nav-link" to="/docs">
            Docs
          </Link>
          <Link className="landing__nav-link" to="/status">
            Status
          </Link>
        </nav>
        <div className="landing__actions">
          <a
            className="landing__nav-link"
            href={DISCORD_INVITE}
            rel="noreferrer"
            target="_blank"
          >
            Join Discord
          </a>
          {signedIn ? (
            <Link to="/app">
              <Button variant="primary">Open dashboard</Button>
            </Link>
          ) : (
            <a href={signInHref}>
              <Button variant="primary">Sign in</Button>
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

export function PublicFooter({ home = false }: ChromeProps): ReactElement {
  const { signInHref, signedIn } = usePublicLinks();
  return (
    <footer className="landing__footer">
      <div className="landing__inner landing__footer-grid">
        <div className="landing__footer-brand">
          <BrandLogo />
          <p className="landing__note">
            Layered bytecode protection for Lua and Luau scripts, built for Roblox.
          </p>
        </div>
        <nav aria-label="Product" className="landing__footer-col">
          <h2 className="landing__footer-title">Product</h2>
          <Link to="/try">Free tool</Link>
          <a href={sectionHref(home, '#features')}>Features</a>
          <a href={sectionHref(home, '#pricing')}>Pricing</a>
          <a href={sectionHref(home, '#faq')}>FAQ</a>
          <Link to="/docs">Docs</Link>
          <Link to="/status">Status</Link>
        </nav>
        <nav aria-label="Legal" className="landing__footer-col">
          <h2 className="landing__footer-title">Legal</h2>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/contact">Contact</Link>
        </nav>
        <nav aria-label="Community" className="landing__footer-col">
          <h2 className="landing__footer-title">Community</h2>
          <a href={DISCORD_INVITE} rel="noreferrer" target="_blank">
            Join Discord
          </a>
          {signedIn ? <Link to="/app">Dashboard</Link> : <a href={signInHref}>Sign in</a>}
        </nav>
      </div>
      <div className="landing__inner landing__footer-base">
        <p className="landing__note">Copyright 2026 LuaCentrix. All rights reserved.</p>
      </div>
    </footer>
  );
}

export function PublicPage({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="landing">
      <PublicTopbar />
      <main className="landing__main landing__main--doc">
        <div className="landing__inner landing__doc">
          <header className="landing__doc-header">
            <h1 className="landing__title landing__title--doc">{title}</h1>
            <p className="landing__lede">{intro}</p>
          </header>
          <div className="landing__prose">{children}</div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
